<template>
  <v-dialog persistent v-model="isOpened">
    <v-card>
      <v-toolbar flat :color="active ? 'primary' : 'grey-darken-1'" dark>
        <template #prepend>
          <v-icon>mdi-calendar-clock</v-icon>
        </template>
        <v-toolbar-title>
          <v-text-field
              variant="outlined"
              density="compact"
              placeholder="스케쥴명"
              hide-details
              v-model="scheduleName"
              @focusout="pendingScheduleName = false"
              @keyup.enter="pendingScheduleName = false"
              v-if="pendingScheduleName"
          ></v-text-field>
          <template v-else>
            <span @click="pendingScheduleName = true">{{ displayScheduleName }}</span>
          </template>
        </v-toolbar-title>
        <template #append>
          <v-switch class="flex-0-0" v-model="active" hide-details inset></v-switch>
        </template>
      </v-toolbar>
      <v-card-item class="schedule-item">
        <v-card-title>시간</v-card-title>
        <v-card-item :class="{selected: type === 'after'}" @click="type='after'">
          <div class="d-flex align-end" style="margin: -8px;">
            <v-select class="ma-2" label="시간" hide-details density="compact" v-model="afterHours" :items="HOURS"></v-select>
            <v-select class="ma-2" label="분" hide-details density="compact" v-model="afterMinutes" :items="TIMES"></v-select>
            <span class="ma-2">뒤</span>
          </div>
        </v-card-item>
        <v-divider></v-divider>
        <v-card-item :class="{selected: type === 'at'}" @click="type='at'">
          <v-row>
            <v-col cols="12" class="d-flex align-center">
              <v-chip-group multiple selected-class="text-warning" class="flex-0-0" v-model="days">
                <v-chip :value="item" v-for="item in DAYS">{{ item.name }}</v-chip>
<!--                <v-btn :value="item" v-for="item in DAYS">{{ item.name }}</v-btn>-->
              </v-chip-group>
              <v-chip hide-details @click="days = (DAYS.length === days.length ?  [] : DAYS)">전체</v-chip>
              <v-spacer></v-spacer>
              <span>{{ 0 === days.length ? '한번' : '반복' }}</span>
            </v-col>
          </v-row>
          <div class="d-flex align-end" style="margin: -8px;">
            <v-select class="ma-2" label="시" hide-details density="compact" v-model="atHours" :items="HOURS"></v-select>
            <v-select class="ma-2" label="분" hide-details density="compact" v-model="atMinutes" :items="TIMES"></v-select>
          </div>
        </v-card-item>
        <v-divider></v-divider>
        <v-card-item :class="{selected: type === 'at'}" @click="type='at'" v-if="false">
          <div class="d-flex align-end" style="margin: -8px;">
            <v-dialog width="auto" v-model="showDialog" v-if="false">
              <template v-slot:activator="{ props }">
                <v-text-field hide-details density="compact" class="ma-2" v-model="date">
                  <template v-slot:append-inner>
                    <v-icon v-bind="props">mdi-calendar</v-icon>
                  </template>
                </v-text-field>
              </template>
              <vue-date-picker v-model="date" inline @update:model-value="showDialog = false">
              </vue-date-picker>
            </v-dialog>
            <v-text-field hide-details density="compact" class="ma-2" v-model="datetime" v-else></v-text-field>
            <span class="ma-2 flex-0-0">예정</span>
          </div>
        </v-card-item>
        <v-card-title class="mt-4">장치</v-card-title>
        <v-card-item>
          <v-chip-group class="d-flex">
            <v-chip hide-details v-for="device in wrappedDevices" @click="onDevice(device)">{{ device.name }}</v-chip>
          </v-chip-group>
        </v-card-item>
        <v-card-title class="mt-4">리모콘</v-card-title>
        <v-card-item>
          <v-row>
            <v-col cols="12" sm="6" md="4" xl="3" class="pa-4" v-for="remocon in remocons">
              <v-remocon :value="remocon" @command="commandId => onCommand(remocon.id, commandId)"></v-remocon>
            </v-col>
          </v-row>
        </v-card-item>
      </v-card-item>
      <v-card-actions class="d-flex">
        <v-btn @click="onCancel">취소</v-btn>
        <v-spacer></v-spacer>
        <v-btn variant="tonal" @click="onOk">저장</v-btn>
      </v-card-actions>
    </v-card>
    <v-devices-dialog v-model="devices" @update:modelValue="dialog.device.isOpened = false" v-if="dialog.device.isOpened"></v-devices-dialog>
  </v-dialog>
</template>

<script setup>
import {computed, defineProps, reactive, ref, toRaw, watch} from 'vue';
import dayjs from 'dayjs';
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css'
import VDevicesDialog from "@/components/VDevicesDialog.vue";
import {useAppStore} from "@/stores/app";
import VRemocon from "@/components/VRemocon.vue";
import {ADD_DEVICE_NAME, DAYS, HOURS, TIMES} from "@/common/constants";

const props = defineProps(['modelValue']);
const emit = defineEmits(['update:modelValue', 'onCancel', 'onOk'])

const appStore = useAppStore();

const pendingScheduleName = ref(false);
const scheduleName = ref(props.modelValue.name);
const active = ref(props.modelValue.active);
const type = ref('after');
const afterHours = ref(3);
const afterMinutes = ref(0);
const atHours = ref(15);
const atMinutes = ref(0);
const days = ref([]);
const date = ref();
const datetime = ref(dayjs().format('YYYY-MM-DD HH:mm'));

const devices = ref(props.modelValue.devices);
const remocons = ref([]);

const isOpened = ref(true);
const showDialog = ref(false);

const dialog = reactive({
  device: {
    isOpened: false,
    selected: [],
  },
});

const displayScheduleName = computed(() => {
  if (scheduleName.value) return scheduleName.value;

  if (type.value === 'after') {
    const time = [
      0 < afterHours.value && `${afterHours.value}시간`,
      0 < afterMinutes.value && `${afterMinutes.value}분`
    ].filter(v => v).join(' ');
    return time && `${time} 뒤` || '';
  }

  if (type.value === 'at') {
    const time = [
      `${atHours.value}시`,
      0 !== atMinutes.value && `${atMinutes.value}분`,
    ].filter(v => v).join(' ');

    const daysText = DAYS.filter(({code}) => days.value.some(day => day.code === code))
        .map(({name}) => name).join(', ');
    return [
      0 === days.value.length ? ''
          : days.value.length === DAYS.length ? '매일'
              : `매주 ${daysText}`,
      time,
    ].join(' ');
  }

  return '';
});

const invalidateCondition = condition => {
  if (!condition) return;

  const [_type, time, _days] = condition.split(',');
  const [hours, minutes] = time.split(':')
  if (_type === 'after') {
    type.value = _type;
    afterHours.value = Number(hours);
    afterMinutes.value = Number(minutes);

  } else if (_type === 'at') {
    type.value = _type;
    atHours.value = Number(hours);
    atMinutes.value = Number(minutes);
    days.value = DAYS.filter(({code}) => _days.includes(code));
  }
};
invalidateCondition(props.modelValue.condition);

const invalidateRemocons = () => {
  const raw = toRaw(
      appStore.remocons.filter(({name}) => props.modelValue.devices.some(({deviceType}) => deviceType === name))
          .map(remocon => ({
            ...remocon,
            commands: remocon.commands.map(command => ({
              ...command,
              loading: props.modelValue.commands.some(({id}) => id === command.id)
            }))
          }))
  );
  remocons.value = JSON.parse(JSON.stringify(raw));
};
invalidateRemocons();

const wrappedDevices = computed(() => [...devices.value, {
  id: ADD_DEVICE_NAME,
  name: '추가',
}]);

watch(afterHours, newValue => {
    afterMinutes.value = 0 === newValue && 0 === afterMinutes.value ? 30 : afterMinutes.value;
})

watch(afterMinutes, newValue => {
  afterHours.value = 0 === newValue && 0 === afterHours.value ? 3 : afterHours.value;
})

watch(devices, newValue => {
  const newRemocons = appStore.remocons.filter(({name}) =>
      newValue.some(({deviceType}) => deviceType === name));
  remocons.value = newRemocons
      .map(remocon => ({...remocon, commands: remocon.commands}))
      .map(remocon => ({
        ...remocon,
        ...remocons.value.find(({id}) => id === remocon.id) || {}
      }));
});

const onDevice = async device => {
  if (device.id !== ADD_DEVICE_NAME) {
    devices.value = devices.value.filter(({id}) => id !== device.id);
    return;
  }
  dialog.device.isOpened = true;
};

const onCommand = (remoconId, commandId) => {
  const remocon = remocons.value.find(({id}) => id === remoconId);
  if (!remocon) return;

  remocon.commands = remocon.commands.map(command => ({
    ...command,
    loading: command.id === commandId ? !command.loading : false,
  }));
};

const verify = () => {
  if (0 === devices.value.length) {
    alert('하나 이상의 장치를 추가해야 합니다.');
    return false;
  }

  const hasUnpressedRemocon = remocons.value.some(remocon => !remocon.commands.some(({loading}) => loading));
  if (hasUnpressedRemocon) {
    alert('리모콘의 버튼을 선택하세요');
    return false;
  }

  return true;
};

const onCancel = () => {
  emit('onCancel', props.modelValue);
  // emit('update:modelValue', props.modelValue);
};

const onOk = () => {
  if (!verify()) return;
  if (props.modelValue.active && !active.value && !confirm('비활성화시 예정시간이 초기화 됩니다. 진행하시겠습니까?')) return;

  const condition = [[
    'after', `${afterHours.value}:${afterMinutes.value}`
  ], [
    'at', `${atHours.value}:${atMinutes.value}`, days.value.map(({code}) => code).join('|')
  ]].find(([t]) => t === type.value).join(',');
  const commands = remocons.value.flatMap(remocon => remocon.commands).filter(({loading}) => loading);
  const payload = {
    ...props.modelValue,
    name: scheduleName.value,
    condition,
    active: active.value,
    devices: devices.value,
    commands,
  };
  // emit('update:modelValue', payload);
  emit('onOk', payload);
  // emit('update:modelValue', {
  //   ...props.modelValue,
  //   name: scheduleName,
  //   condition,
  //   active: active.value,
  //   devices: devices.value,
  //   commands,
  // });
};

</script>

<style scoped>
.selected {
  background-color: bisque;
}
.schedule-item {overflow-x: hidden;overflow-y: auto;}

</style>
